import { chains } from '../data/beheer/chains';
import type { DecisionChain, ExecutionStatus, LogbookEvent, LogFilters, ReactionKind } from '../types/beheer';
import type { LogEntry, Trace } from '../types/db';

const STATUS_BY_OUTCOME: Record<LogEntry['outcome'], ExecutionStatus> = {
  uitgevoerd: 'ok',
  goedgekeurd: 'ok',
  geweigerd: 'geweigerd',
  afgewezen: 'wacht',
  gestopt: 'gestopt',
  gemeld: 'gemeld',
};

const REACTION_BY_OUTCOME: Record<LogEntry['outcome'], { kind: ReactionKind; text: string }> = {
  uitgevoerd: { kind: 'geen', text: 'Geen reactie' },
  goedgekeurd: { kind: 'goedgekeurd', text: 'Jij keurde dit goed' },
  afgewezen: { kind: 'afgewezen', text: 'Jij wees dit af' },
  geweigerd: { kind: 'geen', text: 'Geen reactie' },
  gestopt: { kind: 'teruggedraaid', text: 'Jij drukte op Noodstop' },
  gemeld: { kind: 'gezien', text: 'Melding getoond' },
};

const CHANNEL_BY_LEVEL: Record<LogEntry['level'], string> = {
  zelf: 'Stil · alleen logboek',
  tenzijBezwaar: 'Kaart met afteltijd',
  naOk: 'Bureau · wachtrij',
  nooit: 'Logboek',
};

const REASON_BY_LEVEL: Record<LogEntry['level'], string> = {
  zelf: 'Klein en omkeerbaar, binnen wat Anna zelf mag.',
  tenzijBezwaar: 'Anna doet het, tenzij jij binnen de afteltijd bezwaar maakt.',
  naOk: 'Dit gebeurt alleen na jouw ok.',
  nooit: 'Valt buiten wat Anna mag, ongeacht wie het vraagt.',
};

/** Rebuild a chain from the trace when no full recording exists. */
function reconstruct(entry: LogEntry, trace?: Trace): DecisionChain {
  const agent = entry.agent;
  const action = trace?.action ?? entry.summary;
  return {
    trigger: {
      event: trace?.trigger ?? entry.summary,
      topic: agent === 'systeem' ? 'anna/system/event' : `anna/${agent}/event`,
      timestamp: `2026-10-16T${/^\d\d:\d\d$/.test(entry.time) ? entry.time : '00:00'}:00+02:00`,
      source: agent === 'systeem' ? 'Anna-core' : `${agent[0].toUpperCase()}${agent.slice(1)}-agent`,
    },
    context: trace
      ? [
          { label: 'Huismodus', value: trace.mode },
          { label: 'Aanwezig', value: trace.presence },
          { label: 'Agenda', value: trace.calendar },
          { label: 'Doel', value: trace.goal },
          ...trace.data.map((d) => ({ label: 'Gebruikte data', value: `${d.label} (${d.where})` })),
        ]
      : [{ label: 'Context', value: 'Niet vastgelegd voor deze rij' }],
    candidates: [
      { action, score: 0.8, chosen: true },
      { action: 'Niets doen', score: 0.3 },
    ],
    decision: { level: entry.level, channel: CHANNEL_BY_LEVEL[entry.level], reason: REASON_BY_LEVEL[entry.level] },
    execution: {
      gateway: entry.where === 'cloud' ? 'MCP-gateway → Claude API' : 'MCP-gateway',
      tool: agent === 'huis' ? 'homeassistant.call_service' : `${agent}.action`,
      status: STATUS_BY_OUTCOME[entry.outcome],
      latencyMs: entry.where === 'cloud' ? 1800 : 220,
      where: entry.where,
      detail: entry.summary,
    },
    reaction: REACTION_BY_OUTCOME[entry.outcome],
  };
}

export function buildLogbook(history: LogEntry[], live: LogEntry[], traces: Trace[]): LogbookEvent[] {
  return [...history, ...live]
    .map((entry) => {
      const recorded = entry.traceId ? chains[entry.traceId] : undefined;
      if (recorded) return { entry, chain: recorded, reconstructed: false };
      const trace = entry.traceId ? traces.find((t) => t.id === entry.traceId) : undefined;
      return { entry, chain: reconstruct(entry, trace), reconstructed: true };
    })
    .sort((a, b) => sortKey(b.entry) - sortKey(a.entry));
}

/** Minutes since midnight today; yesterday is negative; live rows without a clock come last-first. */
function sortKey(e: LogEntry): number {
  const m = e.time.match(/(\d{1,2}):(\d{2})/);
  const minutes = m ? Number(m[1]) * 60 + Number(m[2]) : 0;
  const base = e.time.startsWith('gisteren') ? minutes - 1440 : minutes;
  // Live rows (l-100+) share the clock of the moment; keep their insertion order
  const seq = Number(e.id.replace(/\D/g, '')) || 0;
  return base + (e.id.startsWith('l-') ? seq / 10000 : 0);
}

export function filterLogbook(events: LogbookEvent[], f: LogFilters): LogbookEvent[] {
  return events.filter(
    ({ entry }) =>
      (f.agent === 'alle' || entry.agent === f.agent) &&
      (f.level === 'alle' || entry.level === f.level) &&
      (f.outcome === 'alle' || entry.outcome === f.outcome) &&
      (f.where === 'alle' || entry.where === f.where),
  );
}

export function dayGroup(e: LogEntry): 'Gisteravond' | 'Vannacht' | 'Vanochtend' | 'Vandaag' {
  if (e.time.startsWith('gisteren')) return 'Gisteravond';
  const m = e.time.match(/^(\d{1,2}):/);
  const h = m ? Number(m[1]) : 12;
  if (h < 6) return 'Vannacht';
  if (h < 12) return 'Vanochtend';
  return 'Vandaag';
}

export function formatLatency(ms: number): string {
  if (ms < 1000) return `${ms} ms`;
  if (ms < 60000) return `${(ms / 1000).toFixed(1).replace('.', ',')} s`;
  return `${Math.floor(ms / 60000)} min ${Math.round((ms % 60000) / 1000)} s`;
}
